import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { map, take } from 'rxjs';
import { EntitlementService } from '../services/entitlement.service';

/**
 * Route guard that blocks navigation to modules the tenant hasn't paid for.
 *
 * Usage in routes:
 *   {
 *     path: 'some-path',
 *     component: SomeComponent,
 *     canActivate: [entitlementGuard],
 *     data: { entitledSystem: 'Finance' }   // ← module tag, matched like [RequiresPlanModule("Finance")]
 *   }
 *
 * If the route has no `entitledSystem` data key the guard passes through
 * (backward-compatible with existing routes).
 */
export const entitlementGuard: CanActivateFn = (route, _state) => {
  const entitlementService = inject(EntitlementService);
  const router = inject(Router);

  const requiredSystem: string | undefined = route.data?.['entitledSystem'];

  // No entitlement requirement on this route → allow
  if (!requiredSystem) {
    return true;
  }

  // Ensure entitlements are loaded, then check
  return entitlementService.load().pipe(
    take(1),
    map((systems) => {
      // Same matching rule as the backend [RequiresPlanModule] ("nameEn contains tag").
      if (entitlementService.hasModule(requiredSystem, systems)) {
        return true;
      }

      // Not entitled → redirect to dashboard with a query param so the UI
      // can optionally show an "upgrade" toast / banner.
      return router.createUrlTree(['/dashboard'], {
        queryParams: { blocked: requiredSystem },
      });
    })
  );
};
